/**
 * indicadores.ts — Glossário descritivo de todos os indicadores dos dashboards
 *
 * Cada indicador possui:
 * - titulo: nome curto exibido no tooltip
 * - descricao: explicação em linguagem simples (sem fórmulas matemáticas)
 * - interpretacao: como interpretar o valor (o que é bom, o que é ruim)
 * - contexto: onde e quando este indicador é relevante
 * - faixa?: informação sobre a escala do indicador (opcional)
 */

export interface IndicadorDef {
  titulo: string;
  descricao: string;
  interpretacao: string;
  contexto?: string;
  faixa?: string;
}

export const INDICADORES: Record<string, IndicadorDef> = {
  // ─── Indicadores de Participação / Coleta ─────────────────────────────────

  adesao: {
    titulo: "Taxa de Adesão",
    descricao:
      "Percentual de colaboradores que responderam ao questionário de saúde mental em relação ao total esperado.",
    interpretacao:
      "Quanto maior, melhor. Acima de 80% indica boa participação. Abaixo de 50% pode comprometer a representatividade dos resultados.",
    contexto:
      "Calculada com base nas respostas recebidas divididas pelo total de colaboradores incluídos na campanha.",
    faixa: "0% a 100%",
  },

  progresso: {
    titulo: "Progresso das Avaliações",
    descricao:
      "Percentual de avaliações já processadas e concluídas pelo sistema em relação ao total de colaboradores.",
    interpretacao:
      "Reflete o avanço do ciclo NR-1. 100% significa que todos os colaboradores tiveram seus dados analisados pelo motor de avaliação.",
    contexto:
      "Diferente da adesão: um colaborador pode ter respondido (adesão) mas a avaliação ainda estar em processamento (progresso).",
    faixa: "0% a 100%",
  },

  totalRespostas: {
    titulo: "Total de Respostas",
    descricao:
      "Número absoluto de questionários completamente respondidos pelos colaboradores.",
    interpretacao:
      "Quanto maior, mais dados disponíveis para análise. Permite identificar tendências e padrões por setor e cargo.",
    contexto: "Soma de todos os questionários com status de conclusão confirmado.",
  },

  // ─── Indicadores de Risco ─────────────────────────────────────────────────

  owi: {
    titulo: "OWI — Índice de Bem-Estar Ocupacional",
    descricao:
      "Índice que representa o nível geral de bem-estar dos colaboradores da empresa, calculado a partir das respostas ao questionário ERGOS/COPSOQ.",
    interpretacao:
      "Escala de 0 a 100. Acima de 80 = Saudável (verde). Entre 60 e 80 = Moderado (teal). Entre 40 e 60 = Atenção (amarelo). Abaixo de 40 = Crítico (vermelho).",
    contexto:
      "Combina múltiplos fatores: nível de risco, cobertura de colaboradores avaliados e distribuição de resultados positivos.",
    faixa: "0 a 100",
  },

  pulso: {
    titulo: "Pulso da Empresa",
    descricao:
      "Diagnóstico geral do clima psicossocial da empresa, baseado nos padrões de resposta identificados nas avaliações.",
    interpretacao:
      "Indica se o ambiente de trabalho está saudável, em atenção ou em situação crítica. A tendência (↑ melhora, ↓ piora, → estável) mostra a evolução ao longo do tempo.",
    contexto:
      "Calculado a partir da distribuição dos qualificadores de risco (Aceitável, Atenção, Incerto, Inaceitável) entre os colaboradores avaliados.",
    faixa: "Baixo Risco / Médio Risco / Alto Risco",
  },

  qualificador: {
    titulo: "Qualificador de Risco",
    descricao:
      "Classificação qualitativa do nível de risco psicossocial identificado para cada combinação de setor e cargo.",
    interpretacao:
      "Aceitável: risco baixo, sem necessidade de ação imediata. Atenção: requer monitoramento. Incerto: dados insuficientes para conclusão definitiva. Inaceitável: risco elevado, exige intervenção prioritária.",
    contexto:
      "Baseado na pontuação ERGOS. Cada par setor×cargo recebe uma classificação independente.",
    faixa: "Aceitável / Atenção / Incerto / Inaceitável",
  },

  classificacaoRisco: {
    titulo: "Classificação de Risco (P×S)",
    descricao:
      "Matriz de risco que combina a Probabilidade de ocorrência com a Severidade do impacto para cada fator psicossocial.",
    interpretacao:
      "Quanto mais à direita e acima na matriz, maior o risco. Utilizada para priorizar ações de intervenção e compor o documento NR-1.",
    contexto:
      "Metodologia baseada na NR-1 (2025): avalia probabilidade de manifestação do risco e severidade das consequências para a saúde.",
    faixa: "Baixo / Médio / Alto / Crítico",
  },

  setoresAlerta: {
    titulo: "Setores em Alerta",
    descricao:
      "Número de setores da empresa que apresentaram nível de risco psicossocial elevado nas avaliações.",
    interpretacao:
      "Zero setores em alerta é o ideal. Cada setor em alerta indica necessidade de ação imediata, como intervenções, treinamentos ou revisão de processos.",
    contexto:
      "Um setor é classificado em alerta quando sua pontuação média de risco supera o limiar de risco alto definido pela metodologia ERGOS.",
  },

  custoNaoAgir: {
    titulo: "Custo do Não-Agir",
    descricao:
      "Estimativa financeira do impacto anual caso os riscos psicossociais identificados não sejam tratados, considerando potenciais afastamentos e queda de produtividade.",
    interpretacao:
      "Quanto maior o valor, maior o incentivo financeiro para agir. Este número ajuda a justificar investimentos em saúde mental para a liderança.",
    contexto:
      "Estimativa baseada em premissas de mercado: percentual de colaboradores em risco alto com probabilidade de afastamento e custo médio por afastamento.",
    faixa: "R$ 0+",
  },

  // ─── Indicadores Operacionais do Canal ────────────────────────────────────

  saudeConta: {
    titulo: "Saúde da Conta",
    descricao:
      "Índice composto que mede a saúde operacional de um cliente, considerando participação, avanço das avaliações, documentos gerados e ausência de bloqueios.",
    interpretacao:
      "Acima de 70: conta saudável. Entre 40 e 70: atenção necessária. Abaixo de 40: conta em risco, requer intervenção do consultor.",
    contexto:
      "Calculado automaticamente pelo sistema com base em dados reais de cada empresa cliente. Atualizado a cada ciclo de avaliação.",
    faixa: "0 a 100",
  },

  etapaJornada: {
    titulo: "Etapa da Jornada",
    descricao:
      "Indica em qual fase do ciclo NR-1 o cliente se encontra atualmente: desde a importação inicial dos dados até a geração do documento final.",
    interpretacao:
      "Não Iniciado → Importação → Campanha → Coleta → Avaliação → Documento. Quanto mais avançada a etapa, mais próximo da conformidade NR-1.",
    contexto:
      "Permite ao consultor identificar rapidamente onde cada cliente está travado e qual é o próximo passo a ser tomado.",
    faixa: "6 etapas sequenciais",
  },

  clientesAtivos: {
    titulo: "Clientes Ativos",
    descricao:
      "Número de empresas clientes com pelo menos uma campanha ou avaliação em andamento no período atual.",
    interpretacao:
      "Reflete o volume operacional do canal. Clientes inativos podem indicar necessidade de reengajamento.",
    contexto: "Contagem de empresas com atividade registrada no sistema.",
  },

  clientesAlerta: {
    titulo: "Clientes em Alerta",
    descricao:
      "Número de clientes que apresentam alguma situação que requer atenção imediata: baixa adesão, avaliações bloqueadas ou prazo crítico.",
    interpretacao:
      "Zero é o ideal. Cada cliente em alerta deve ser priorizado pelo consultor para evitar atrasos no cumprimento da NR-1.",
    contexto:
      "Um cliente entra em alerta quando a adesão está abaixo do mínimo necessário, há avaliações bloqueadas por falta de dados, ou o prazo de entrega está próximo.",
  },

  adesaoMedia: {
    titulo: "Adesão Média do Canal",
    descricao:
      "Média da taxa de adesão de todos os clientes ativos do canal, ponderada pelo número de colaboradores.",
    interpretacao:
      "Acima de 60%: bom engajamento geral. Abaixo de 40%: o canal precisa apoiar os clientes na mobilização dos colaboradores.",
    contexto:
      "Indicador de saúde geral do portfólio. Baixa adesão média pode indicar necessidade de treinamento dos clientes sobre como engajar colaboradores.",
    faixa: "0% a 100%",
  },

  // ─── Indicadores de Volume / Contagem ─────────────────────────────────────

  totalClientes: {
    titulo: "Total de Clientes",
    descricao:
      "Número total de empresas clientes cadastradas e gerenciadas pelo canal.",
    interpretacao:
      "Representa o tamanho do portfólio do canal. Crescimento indica expansão comercial.",
    contexto: "Inclui todos os clientes, independentemente do estágio na jornada.",
  },

  totalAvaliacoes: {
    titulo: "Total de Avaliações",
    descricao:
      "Número total de avaliações psicossociais registradas, incluindo todas as combinações de setor×cargo de todos os clientes.",
    interpretacao:
      "Cada avaliação representa um par setor×cargo analisado. Quanto maior, mais abrangente é o mapeamento de risco.",
    contexto:
      "Uma empresa com 5 setores e 3 cargos por setor gera até 15 avaliações distintas.",
  },

  avaliacoesConcluidas: {
    titulo: "Avaliações Concluídas",
    descricao:
      "Número de avaliações que foram processadas com sucesso pelo motor de análise e têm resultado disponível.",
    interpretacao:
      "Quanto maior a proporção de concluídas em relação ao total, mais completo é o diagnóstico. Avaliações bloqueadas precisam de atenção.",
    contexto:
      "Uma avaliação é concluída quando há respostas suficientes (acima do kMin) e o motor Ergos processou os dados com sucesso.",
  },

  documentos: {
    titulo: "Documentos Gerados",
    descricao:
      "Número de documentos NR-1 (GRO — Gerenciamento de Riscos Ocupacionais) gerados com sucesso para os clientes.",
    interpretacao:
      "O documento NR-1 é o produto final do ciclo. Cada documento representa uma empresa em conformidade com a legislação.",
    contexto:
      "Gerado automaticamente após a conclusão das avaliações. Inclui mapa de riscos, plano de ação e evidências de conformidade.",
  },

  // ─── Indicadores de Pipeline ──────────────────────────────────────────────

  pipelinePendente: {
    titulo: "Pendente",
    descricao:
      "Avaliações aguardando o início da coleta de respostas. O questionário ainda não foi enviado ou ativado.",
    interpretacao:
      "Alta quantidade de pendentes pode indicar que campanhas precisam ser ativadas.",
    contexto: "Status inicial de toda avaliação após a importação dos dados.",
  },

  pipelineColetaAndamento: {
    titulo: "Em Coleta",
    descricao:
      "Avaliações com campanha ativa: o questionário foi enviado e está aguardando respostas dos colaboradores.",
    interpretacao:
      "É o estágio ativo de participação. Monitorar a taxa de adesão neste estágio é essencial.",
    contexto: "A campanha está ativa e os colaboradores podem responder.",
  },

  pipelineConcluida: {
    titulo: "Concluída",
    descricao:
      "Avaliações que foram processadas com sucesso e têm resultado disponível para análise e geração de documento.",
    interpretacao:
      "Quanto maior a proporção de concluídas, mais avançado está o ciclo NR-1.",
    contexto:
      "Requer número mínimo de respostas (kMin) e processamento bem-sucedido pelo motor Ergos.",
  },

  pipelineBloqueada: {
    titulo: "Bloqueada",
    descricao:
      "Avaliações que não puderam ser processadas por falta de respostas suficientes ou dados incompletos.",
    interpretacao:
      "Avaliações bloqueadas precisam de ação: reengajar colaboradores para responder ou revisar os dados importados.",
    contexto:
      "Pode ocorrer por: (1) número de respostas abaixo do mínimo necessário (kMin), ou (2) dados insuficientes para análise estatística.",
  },

  // ─── Indicadores de Campanhas ─────────────────────────────────────────────

  campanhasAtivas: {
    titulo: "Campanhas Ativas",
    descricao:
      "Campanhas de questionário atualmente abertas, com link disponível para os colaboradores responderem.",
    interpretacao:
      "Campanhas ativas estão em período de coleta. Monitorar a adesão diariamente é recomendado.",
    contexto:
      "Uma campanha ativa tem data de início definida, link de acesso gerado e está dentro do período de resposta.",
  },

  campanhasEncerradas: {
    titulo: "Campanhas Encerradas",
    descricao:
      "Campanhas que foram finalizadas, seja por atingir a data limite ou por encerramento manual.",
    interpretacao:
      "Campanhas encerradas não aceitam mais respostas. Os dados coletados ficam disponíveis para análise.",
    contexto:
      "Após o encerramento, o motor de avaliação processa os dados e gera os resultados.",
  },

  campanhasRascunho: {
    titulo: "Campanhas em Rascunho",
    descricao:
      "Campanhas criadas mas ainda não ativadas. O questionário ainda não foi disponibilizado para os colaboradores.",
    interpretacao:
      "Rascunhos precisam ser ativados para iniciar a coleta. Verifique se as configurações estão corretas antes de ativar.",
    contexto:
      "Estado inicial de uma campanha. Permite revisar configurações antes de disponibilizar o link.",
  },

  // ─── Indicadores de Setores ───────────────────────────────────────────────

  totalSetores: {
    titulo: "Total de Setores",
    descricao:
      "Número de setores organizacionais distintos mapeados na empresa e incluídos no processo de avaliação.",
    interpretacao:
      "Quanto maior a cobertura de setores, mais representativo é o diagnóstico de risco psicossocial.",
    contexto:
      "Cada setor pode ter múltiplos cargos, gerando avaliações independentes por combinação setor×cargo.",
  },

  // ─── Indicadores do Mapa de Calor ─────────────────────────────────────────

  mapaCalor: {
    titulo: "Mapa de Calor Organizacional",
    descricao:
      "Visualização que mostra o nível de risco psicossocial de cada setor da empresa, usando cores para indicar a intensidade.",
    interpretacao:
      "Verde = baixo risco. Amarelo/laranja = risco moderado, requer atenção. Vermelho = alto risco, requer intervenção prioritária.",
    contexto:
      "Permite identificar rapidamente quais setores concentram os maiores riscos e priorizar as ações do plano de intervenção.",
  },
};

/**
 * Retorna a definição de um indicador pelo seu identificador.
 * Retorna undefined se o indicador não for encontrado.
 */
export function getIndicador(key: string): IndicadorDef | undefined {
  return INDICADORES[key];
}
